import React, { useState, useMemo } from "react";
import { Search, Plus, Minus } from "lucide-react";
import BookDemoModal from "../components/BookDemoModal";
import DividerMotif from "../components/Brand/DividerMotif";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /help-center.
 *
 * Distinctive move: FAQ as article. The eight questions are not 16px
 * accordion items. They are display-size Fraunces titles, numbered like
 * an essay collection, and the page reads as a long-form reference essay
 * rather than a support widget. The search bar is small and sits in the
 * frame, the categories and articles support the questions instead of
 * competing with them.
 */

const CATEGORIES = [
  { title: "Posting a job & tasks", description: "Write a JD, get AI-drafted tasks" },
  { title: "Applying & submissions", description: "What candidates build and send" },
  { title: "AI pre-screening", description: "How submissions get scored and flagged" },
  { title: "Account & access", description: "Sign-in, roles, and your account" },
  { title: "Privacy & data", description: "How we handle resumes and submissions" },
  { title: "Troubleshooting", description: "Common issues and fixes" },
];

const POPULAR_TOPICS = [
  { title: "How AI drafts tasks from your job description", category: "Posting a job & tasks" },
  { title: "What candidates submit, and why it's the application", category: "Applying & submissions" },
  { title: "How AI pre-screening works, and why it never rejects", category: "AI pre-screening" },
  { title: "Editing or writing your own tasks instead of the AI draft", category: "Posting a job & tasks" },
  { title: "Why every submission needs a resume attached", category: "Applying & submissions" },
];

const FAQS = [
  {
    question: "How does AI task generation work?",
    answer:
      "Post the job and Velaigal reads the description, then drafts two to three real-world tasks the role actually involves, technical or not. The same tasks go to every applicant, so candidates are compared on the same basis instead of whoever gets the harder interviewer.",
  },
  {
    question: "Can I edit the tasks, or write my own?",
    answer:
      "Yes. The AI draft is a starting point, not a contract. Edit any task, rewrite it entirely, or mix your own tasks in alongside the generated ones. Whatever you publish is what every applicant sees, unchanged, so the comparison stays fair.",
  },
  {
    question: "What does \"submission = application\" mean?",
    answer:
      "There's no separate application form. A candidate does the tasks and submits the work, a hosted link, a GitHub repo, a document, or a video, whatever the task calls for, and that submission is the application. Resume required, attached alongside the work.",
  },
  {
    question: "What exactly do candidates submit?",
    answer:
      "Whatever the task produces: a link to something they built and hosted, a repository, a written document, or a short video walkthrough. Non-technical roles get non-technical tasks, so the submission format follows the work, not a fixed template.",
  },
  {
    question: "How does AI pre-screening work?",
    answer:
      "Hosted links get opened and screenshotted in a sandboxed browser so you can see the working app without running it yourself. Repos and documents get reviewed against the job's rubric, which stays private and is never shown to candidates. Every submission is scored and flagged.",
  },
  {
    question: "Can the AI reject a candidate automatically?",
    answer:
      "No. Pre-screening scores and flags, it never rejects. Every submission lands in your applicant list with the AI's notes attached, and a human, you, makes every shortlist or pass decision.",
  },
  {
    question: "Is Velaigal free to use?",
    answer:
      "Yes. Velaigal is free during early access, for both posting jobs and applying to them. No pricing tier is gating the core flow right now.",
  },
  {
    question: "How do you handle resumes and submission data?",
    answer:
      "Resumes and submissions are stored to run the job they were submitted for and nothing else. Full detail, including what we store and for how long, is at /privacy.",
  },
];

const FRAUNCES_AXES = { fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' } as const;

const HelpCenter: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useDocumentMeta(
    "Help center · Velaigal",
    "Answers to the questions that come up most about posting jobs, AI-drafted tasks, submissions, and AI pre-screening on Velaigal."
  );

  const filteredTopics = useMemo(() => {
    if (!searchTerm.trim()) return POPULAR_TOPICS;
    const q = searchTerm.toLowerCase();
    return POPULAR_TOPICS.filter(
      (a) => a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q)
    );
  }, [searchTerm]);

  const filteredFaqs = useMemo(() => {
    if (!searchTerm.trim()) return FAQS;
    const q = searchTerm.toLowerCase();
    return FAQS.filter(
      (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [searchTerm]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return CATEGORIES;
    const q = searchTerm.toLowerCase();
    return CATEGORIES.filter(
      (c) => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
    );
  }, [searchTerm]);

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Hero ---------------- */}
      <section className="max-w-7xl mx-auto px-8 pt-20 pb-12">
        <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
          Help center
        </span>
        <h1
          className="font-display text-5xl lg:text-6xl font-medium text-ink leading-tight tracking-tight mt-6 max-w-3xl"
          style={FRAUNCES_AXES}
        >
          The questions that come up most.
        </h1>
        <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-8" />
        <p className="mt-8 text-lg text-ink-secondary leading-relaxed max-w-2xl">
          A reading shelf, not a ticket queue. Eight questions we answer
          almost every day, written out the way we'd answer them on a call.
        </p>

        <div className="mt-10 max-w-xl">
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-tertiary pointer-events-none"
              strokeWidth={1.75}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter the page"
              className="input pl-11 text-base"
            />
          </div>
          <p className="mt-3 text-xs text-ink-tertiary">
            Filters the questions, articles and topics below.
          </p>
        </div>
      </section>

      <DividerMotif className="my-4" />

      {/* ---------------- FAQ as article (the anchor) ---------------- */}
      <section className="max-w-7xl mx-auto px-8 pt-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Sticky meta column */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-[96px]">
              <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                Eight answers
              </span>
              <p
                className="mt-6 font-display text-2xl font-medium text-ink leading-snug"
                style={FRAUNCES_AXES}
              >
                Click any question to read the full reply.
              </p>
              <p className="mt-4 text-sm text-ink-tertiary leading-relaxed">
                {filteredFaqs.length} of {FAQS.length} showing
                {searchTerm.trim() ? ` for "${searchTerm.trim()}"` : ""}.
              </p>
              {searchTerm.trim() && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="mt-4 velaigal-link text-sm font-medium"
                >
                  Clear filter
                </button>
              )}
            </div>
          </aside>

          {/* Question essays */}
          <div className="lg:col-span-9">
            {filteredFaqs.length === 0 ? (
              <div className="border-t border-hairline pt-10">
                <p className="text-base text-ink-secondary">
                  No questions match that filter. Try a shorter term, or write to
                  us directly below.
                </p>
              </div>
            ) : (
              <ol className="divide-y divide-hairline">
                {filteredFaqs.map((faq, index) => {
                  const isOpen = openFAQ === index;
                  const ordinal = String(index + 1).padStart(2, "0");
                  return (
                    <li key={faq.question} className="py-10 first:pt-0">
                      <button
                        type="button"
                        onClick={() => setOpenFAQ(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        aria-controls={`faq-answer-${index}`}
                        className="w-full text-left group block"
                      >
                        <div className="flex items-baseline gap-6">
                          <span
                            className="font-display text-xl font-normal text-ink-tertiary tabular-nums shrink-0"
                            style={FRAUNCES_AXES}
                            aria-hidden="true"
                          >
                            {ordinal}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h2
                              className="velaigal-headline-hover font-display text-3xl md:text-4xl font-medium text-ink leading-[1.15] tracking-tight group-hover:text-saffron transition-colors"
                              style={FRAUNCES_AXES}
                            >
                              {faq.question}
                            </h2>
                          </div>
                          <span
                            className="shrink-0 mt-2 inline-flex items-center justify-center h-7 w-7 border border-hairline text-ink-tertiary group-hover:border-saffron group-hover:text-saffron transition-colors"
                            aria-hidden="true"
                          >
                            {isOpen ? (
                              <Minus className="h-3.5 w-3.5" strokeWidth={1.75} />
                            ) : (
                              <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
                            )}
                          </span>
                        </div>
                      </button>

                      {isOpen && (
                        <div
                          id={`faq-answer-${index}`}
                          className="mt-6 pl-0 md:pl-[3.25rem]"
                        >
                          <p className="text-lg text-ink-secondary leading-[1.7] max-w-2xl">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </section>

      <DividerMotif />

      {/* ---------------- Browse + most read (supporting) ---------------- */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Browse by topic
            </span>
            <h2
              className="velaigal-headline-hover mt-6 font-display text-3xl font-medium text-ink leading-snug"
              style={FRAUNCES_AXES}
            >
              Six shelves of writing.
            </h2>
            <p className="mt-4 text-base text-ink-secondary leading-relaxed max-w-md">
              Walk the categories to find the shelf that matches your
              question.
            </p>
          </div>

          <div className="lg:col-span-7">
            {filteredCategories.length === 0 ? (
              <p className="text-sm text-ink-tertiary border-t border-hairline pt-6">
                No topics match that filter.
              </p>
            ) : (
              <ul className="divide-y divide-hairline border-t border-hairline">
                {filteredCategories.map((category) => (
                  <li key={category.title}>
                    <button
                      type="button"
                      className="w-full py-6 flex items-baseline justify-between gap-6 text-left group"
                    >
                      <div className="min-w-0">
                        <h3
                          className="font-display text-2xl font-medium text-ink leading-snug group-hover:text-saffron transition-colors"
                          style={FRAUNCES_AXES}
                        >
                          {category.title}
                        </h3>
                        <p className="mt-2 text-sm text-ink-secondary leading-relaxed">
                          {category.description}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <DividerMotif />

      {/* ---------------- A few more topics ---------------- */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              A few more
            </span>
            <h2
              className="velaigal-headline-hover mt-6 font-display text-3xl font-medium text-ink leading-snug"
              style={FRAUNCES_AXES}
            >
              Topics we get asked about.
            </h2>
          </div>

          <div className="lg:col-span-8">
            {filteredTopics.length === 0 ? (
              <p className="text-sm text-ink-tertiary border-t border-hairline pt-6">
                No topics match that filter.
              </p>
            ) : (
              <ol className="divide-y divide-hairline border-t border-hairline">
                {filteredTopics.map((topic, index) => (
                  <li key={topic.title} className="py-6">
                    <div className="flex items-baseline gap-6">
                      <span
                        className="font-display text-base font-normal text-ink-tertiary tabular-nums shrink-0"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-display text-xl md:text-2xl font-medium text-ink leading-snug"
                          style={FRAUNCES_AXES}
                        >
                          {topic.title}
                        </h3>
                        <div className="mt-2 flex items-center gap-3 text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                          <span>{topic.category}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </section>

      <DividerMotif />

      {/* ---------------- Still need help (support channels) ---------------- */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
          Still need help?
        </span>
        <h2
          className="velaigal-headline-hover mt-6 font-display text-3xl md:text-4xl font-medium text-ink leading-snug max-w-2xl"
          style={FRAUNCES_AXES}
        >
          Talk to a person, not a chatbot.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-hairline border-t border-b border-hairline">
          <a
            href="https://wa.me/918220243513"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-cream p-10 group no-underline"
            style={{ textDecoration: "none" }}
          >
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              WhatsApp
            </span>
            <h3
              className="font-display text-2xl font-medium text-ink leading-snug mt-4 group-hover:text-saffron transition-colors"
              style={FRAUNCES_AXES}
            >
              Send us a message
            </h3>
            <p className="mt-3 text-sm text-ink-secondary leading-relaxed">
              India business hours.
            </p>
          </a>
          <a
            href="mailto:im.vmanoj2002@gmail.com"
            className="bg-cream p-10 group no-underline"
            style={{ textDecoration: "none" }}
          >
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Email
            </span>
            <h3
              className="font-display text-2xl font-medium text-ink leading-snug mt-4 group-hover:text-saffron transition-colors"
              style={FRAUNCES_AXES}
            >
              im.vmanoj2002@gmail.com
            </h3>
            <p className="mt-3 text-sm text-ink-secondary leading-relaxed">
              One business day response.
            </p>
          </a>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="bg-cream p-10 text-left group"
          >
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Demo
            </span>
            <h3
              className="font-display text-2xl font-medium text-ink leading-snug mt-4 group-hover:text-saffron transition-colors"
              style={FRAUNCES_AXES}
            >
              Book a walkthrough
            </h3>
            <p className="mt-3 text-sm text-ink-secondary leading-relaxed">
              Thirty minutes with a real engineer.
            </p>
          </button>
        </div>
      </section>

      <Footer />
      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HelpCenter;
